import React, { useState, useEffect, useMemo } from "react";
import { useDispatch } from "react-redux";
import { useLocation } from "react-router-dom";
import { useSelector } from "store";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import TourComponent from "../../utils/guided-tour/TourComponent";
import { storage } from "../../utils/helpers/storage";
import { CheckAccess } from "../../utils/helpers";
import {
  SUPER_ADMIN,
  LOCUS_USER_MANAGER,
  LOCUS_CUSTOMER_USER_MANAGER,
  USER_MANAGER,
} from "../../utils/constants/user";
import {
  baseTourComponentSteps,
  dashboardReportTourComponentSteps,
  multiSiteTourComponentSteps,
} from "../../utils/guided-tour/IntroTourSteps";
import { hasAccessToAllSites } from "../../routes/ProtectedRoute";
import { editUser, setActiveUser } from "store/slices/user";

const IntroTourComponent = () => {
  const location = useLocation();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));
  const dispatch = useDispatch();
  const [isTourRunning, setIsTourRunning] = useState(false);
  const [steps, setSteps] = useState([]);
  const [isDashboardOrReports, setIsDashboardOrReports] = useState(false);
  const { activeUser, viewState } = useSelector((state) => state.user);
  const { kioskMode } = useSelector((state) => state.menu);

  const updateUser = async (user) => {
    let updateUser = await dispatch(editUser(activeUser.id, user, true));
    if (updateUser && updateUser?.status === 200) {
      dispatch(setActiveUser({ ...activeUser, ...user }));
    }
  };

  const showAllSites = useMemo(() => {
    return hasAccessToAllSites(activeUser?.groups);
  }, [activeUser]);

  useEffect(() => {
    const dashboardOrReportsPage =
      location.pathname.toLowerCase().includes("dashboards") ||
      location.pathname.toLowerCase().includes("reports");

    if (
      isDesktop &&
      activeUser &&
      (!activeUser?.tours?.completedMainTour ||
        !activeUser?.tours?.completedAnalyticsTour)
    ) {
      let newSteps = [];

      if (
        dashboardOrReportsPage &&
        !activeUser?.tours?.completedAnalyticsTour &&
        !activeUser?.tours?.completedMainTour
      ) {
        newSteps = [
          ...baseTourComponentSteps,
          ...dashboardReportTourComponentSteps,
          ...(showAllSites ? multiSiteTourComponentSteps : []),
        ];
      } else if (
        !dashboardOrReportsPage &&
        !activeUser?.tours?.completedMainTour
      ) {
        newSteps = [
          ...baseTourComponentSteps,
          ...(showAllSites ? multiSiteTourComponentSteps : []),
        ];
      } else if (
        dashboardOrReportsPage &&
        !activeUser?.tours?.completedAnalyticsTour
      ) {
        newSteps = [...dashboardReportTourComponentSteps];
      }

      if (
        newSteps?.length &&
        !CheckAccess(
          [
            SUPER_ADMIN,
            LOCUS_USER_MANAGER,
            LOCUS_CUSTOMER_USER_MANAGER,
            USER_MANAGER,
          ],
          activeUser
        )
      ) {
        newSteps = newSteps.filter(
          (step) => step.target !== ".guided-tour-nav-admin-users"
        );
      }

      setSteps(newSteps);
      setIsTourRunning(newSteps.length > 0);
    }
    setIsDashboardOrReports(dashboardOrReportsPage);
  }, [location, activeUser]);

  // Logic to determine if the tour should run
  const shouldShowTour = useMemo(() => {
    return (
      activeUser &&
      !kioskMode &&
      viewState !== "locus" &&
      isDesktop &&
      !storage.getLocus()
    );
  }, [activeUser, kioskMode, viewState, isDashboardOrReports, isDesktop]);

  return (
    <>
      {shouldShowTour && (
        <TourComponent
          run={isTourRunning}
          steps={steps}
          stepScrollBottom=".guided-tour-nav-admin-users"
          stepScrollTop=".guided-tour-help-icon"
          onComplete={() => {
            /// TODO remove when backend is ready
            let user = {};
            user.tours = {};
            user.tours.completedMainTour = true;
            user.tours.completedAnalyticsTour =
              activeUser.tours?.completedAnalyticsTour || isDashboardOrReports;
            updateUser(user);
            setIsTourRunning(false);
          }}
        />
      )}
    </>
  );
};

export default IntroTourComponent;
